import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Project } from '@/hooks/use-portfolio-data';
import { cn } from '@/lib/utils';
import {
    CheckCircle2,
    Layers,
    ShieldAlert,
    Target,
    Lightbulb,
    Terminal,
    ExternalLink,
    Github,
    ChevronRight,
    ChevronDown,
    Code,
    UserCircle,
    Cpu,
    X,
    Maximize2,
    Activity,
    BrainCircuit,
    GitCompare,
    AlertTriangle
} from 'lucide-react';

interface ProjectDialogProps {
    project: Project | null;
    isOpen: boolean;
    onClose: () => void;
}

export function ProjectDialog({ project, isOpen, onClose }: ProjectDialogProps) {
    const [previewImage, setPreviewImage] = useState<string | null>(null);

    if (!project) return null;

    return (
        <>
            <Dialog open={isOpen} onOpenChange={onClose}>
                <DialogContent className="w-[calc(100%-1.5rem)] sm:w-full max-w-4xl h-[90vh] md:h-[85vh] p-0 overflow-hidden rounded-2xl border-none dark:bg-slate-900 shadow-2xl flex flex-col">
                    <ScrollArea className="flex-1 min-h-0" type="auto">
                        {/* Banner Image with Full Preview Ability */}
                        {project.image ? (
                            <div
                                className="relative h-48 md:h-64 overflow-hidden shrink-0 cursor-zoom-in group/banner"
                                onClick={() => setPreviewImage(project.image)}
                            >
                                {/* Blurry background for portrait images */}
                                <div
                                    className="absolute inset-0 bg-cover bg-center blur-xl opacity-30 scale-110"
                                    style={{ backgroundImage: `url(${project.image})` }}
                                />
                                <img
                                    src={project.image}
                                    alt={project.title}
                                    className="relative w-full h-full object-contain z-10 p-4 md:p-8"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent z-20" />

                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/banner:opacity-100 transition-opacity z-30">
                                    <div className="bg-black/50 text-white px-4 py-2 rounded-full flex items-center gap-2 backdrop-blur-sm border border-white/10">
                                        <Maximize2 className="h-4 w-4" />
                                        <span className="text-xs font-bold uppercase tracking-widest">Preview Hero Image</span>
                                    </div>
                                </div>

                                {project.isNDA && (
                                    <div className="absolute top-4 right-4 animate-fade-in z-30">
                                        <Badge variant="destructive" className="flex items-center gap-1.5 px-3 py-1 font-bold shadow-lg">
                                            <ShieldAlert className="h-3.5 w-3.5" />
                                            NDA PROTECTED
                                        </Badge>
                                    </div>
                                )}
                            </div>
                        ) : null}

                        {/* Content Container */}
                        <div className={cn("px-6 flex flex-col relative z-20", project.image ? "-mt-8" : "pt-6")}>
                            <DialogHeader className="mb-6 bg-background/50 backdrop-blur-sm rounded-xl p-4 -mx-4">
                                <div className="flex flex-wrap gap-2 mb-3">
                                    {project.technologies.map(tech => (
                                        <Badge key={tech} variant="secondary" className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-350 border border-slate-200 dark:border-slate-700 text-[10px] font-bold px-2 py-0.5 uppercase tracking-wider">
                                            {tech}
                                        </Badge>
                                    ))}
                                    {!project.image && project.isNDA && (
                                        <Badge variant="destructive" className="flex items-center gap-1.5 px-3 py-1 font-bold shadow-lg">
                                            <ShieldAlert className="h-3.5 w-3.5" />
                                            NDA PROTECTED
                                        </Badge>
                                    )}
                                </div>
                                <DialogTitle className="text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white leading-tight">{project.title}</DialogTitle>
                                <DialogDescription className="text-base text-slate-600 dark:text-slate-400 mt-2 leading-relaxed font-medium">
                                    {project.description}
                                </DialogDescription>
                            </DialogHeader>

                            <div className="space-y-12 pb-10">

                                {/* Role Section */}
                                {project.role && (
                                    <section>
                                        <div className="flex items-center gap-2 mb-4">
                                            <UserCircle className="h-5 w-5 text-primary" />
                                            <h4 className="font-bold text-lg text-slate-900 dark:text-white">Professional Role</h4>
                                        </div>
                                        <div className="bg-primary/5 border border-primary/10 rounded-xl px-5 py-3 inline-block">
                                            <span className="text-primary font-bold">{project.role}</span>
                                        </div>
                                        <div className="mt-8 border-b border-slate-100 dark:border-slate-800" />
                                    </section>
                                )}

                                {/* Tech Stack Categories */}
                                {project.techStackCategories && (
                                    <section>
                                        <div className="flex items-center gap-2 mb-6">
                                            <Cpu className="h-5 w-5 text-primary" />
                                            <h4 className="font-bold text-lg text-slate-900 dark:text-white">Tech Stack</h4>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 bg-slate-50 dark:bg-slate-800/20 p-6 rounded-2xl border border-slate-100 dark:border-slate-800">
                                            {project.techStackCategories.map((cat, idx) => (
                                                <div key={idx} className="space-y-2">
                                                    <span className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">{cat.label}</span>
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {cat.technologies.map(tech => (
                                                            <span key={tech} className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                                                                {tech}{cat.technologies.indexOf(tech) < cat.technologies.length - 1 ? " • " : ""}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="mt-8 border-b border-slate-100 dark:border-slate-800" />
                                    </section>
                                )}

                                {/* Problem & Solution */}
                                {(project.problem || project.solution) && (
                                    <section>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                            {project.problem && (
                                                <div className="space-y-4">
                                                    <h4 className="flex items-center gap-2 text-slate-900 dark:text-white font-bold text-lg">
                                                        <Target className="h-5 w-5 text-primary" />
                                                        The Problem
                                                    </h4>
                                                    <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed font-medium">
                                                        {project.problem}
                                                    </p>
                                                </div>
                                            )}
                                            {project.solution && (
                                                <div className="space-y-4">
                                                    <h4 className="flex items-center gap-2 text-slate-900 dark:text-white font-bold text-lg">
                                                        <Lightbulb className="h-5 w-5 text-primary" />
                                                        The Solution
                                                    </h4>
                                                    <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed font-medium">
                                                        {project.solution}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                        <div className="mt-10 border-b border-slate-100 dark:border-slate-800" />
                                    </section>
                                )}

                                {/* Challenges Section */}
                                {project.challenges && (
                                    <section>
                                        <div className="flex items-center gap-2 mb-4">
                                            <AlertTriangle className="h-5 w-5 text-amber-500" />
                                            <h4 className="font-bold text-lg text-slate-900 dark:text-white">Engineering Challenges</h4>
                                        </div>
                                        <p className="text-sm text-slate-700 dark:text-slate-350 leading-relaxed font-medium">
                                            {project.challenges}
                                        </p>
                                        <div className="mt-8 border-b border-slate-100 dark:border-slate-800" />
                                    </section>
                                )}

                                {/* Production Metrics Stat Cards */}
                                {project.metrics && (
                                    <section>
                                        <div className="flex items-center gap-2 mb-6">
                                            <Activity className="h-5 w-5 text-primary" />
                                            <h4 className="font-bold text-lg text-slate-900 dark:text-white">Production Metrics</h4>
                                        </div>
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                            {project.metrics.map((metric, idx) => (
                                                <div key={idx} className="p-4 rounded-xl border border-muted/50 bg-slate-50/50 dark:bg-slate-800/30 text-center card-hover transition-transform">
                                                    <span className="text-[10px] text-muted-foreground uppercase tracking-wider block mb-1 font-mono font-bold">Metric Value</span>
                                                    <span className="text-base font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">{metric}</span>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="mt-8 border-b border-slate-100 dark:border-slate-800" />
                                    </section>
                                )}

                                {/* Architecture */}
                                {(project.architecture?.length ?? 0) > 0 && (
                                    <section>
                                        <h4 className="flex items-center gap-2 font-bold mb-6 text-xl text-slate-900 dark:text-white">
                                            <Layers className="h-5 w-5 text-primary" />
                                            System Architecture
                                        </h4>
                                        <div className="w-full overflow-x-auto py-2">
                                            <div className="flex flex-col md:flex-row items-center justify-start md:justify-center gap-4 py-8 bg-slate-50 dark:bg-slate-800/30 rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 px-6 min-w-max md:min-w-0">
                                                {project.architecture?.map((step, idx) => (
                                                    <div key={idx} className="flex flex-col md:flex-row items-center gap-3">
                                                        <div className="px-6 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs sm:text-sm font-mono font-bold text-slate-800 dark:text-slate-200 shadow-md text-center max-w-full whitespace-nowrap">
                                                            {step}
                                                        </div>
                                                        {idx < (project.architecture?.length ?? 0) - 1 && (
                                                            <>
                                                                <ChevronRight className="h-5 w-5 text-slate-400 dark:text-slate-600 animate-pulse hidden md:block" />
                                                                <ChevronDown className="h-5 w-5 text-slate-400 dark:text-slate-600 animate-pulse block md:hidden" />
                                                            </>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="mt-10 border-b border-slate-100 dark:border-slate-800" />
                                    </section>
                                )}

                                {/* Engineering Decisions (Cards) */}
                                {project.decisions && (
                                    <section>
                                        <div className="flex items-center gap-2 mb-6">
                                            <BrainCircuit className="h-5 w-5 text-primary" />
                                            <h4 className="font-bold text-lg text-slate-900 dark:text-white">Engineering Decisions</h4>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                            {project.decisions.map((dec, idx) => (
                                                <div key={idx} className="p-6 bg-slate-50 dark:bg-slate-800/40 rounded-2xl border border-slate-100 dark:border-slate-800 flex flex-col justify-between card-hover">
                                                    <div>
                                                        <span className="text-[10px] text-primary font-bold uppercase tracking-wider block mb-2 font-mono">Decision Frame</span>
                                                        <h5 className="font-bold text-slate-850 dark:text-slate-100 text-sm mb-3 leading-snug">
                                                            {dec.question}
                                                        </h5>
                                                    </div>
                                                    <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed pt-3 border-t border-slate-200 dark:border-slate-800 font-medium">
                                                        {dec.answer}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="mt-8 border-b border-slate-100 dark:border-slate-800" />
                                    </section>
                                )}

                                {/* Architecture Trade-offs (Side-by-side comparison cards) */}
                                {project.tradeoffs && (
                                    <section>
                                        <div className="flex items-center gap-2 mb-6">
                                            <GitCompare className="h-5 w-5 text-primary" />
                                            <h4 className="font-bold text-lg text-slate-900 dark:text-white">Architecture Trade-offs</h4>
                                        </div>
                                        <div className="space-y-6">
                                            {project.tradeoffs.map((trade, idx) => (
                                                <div key={idx} className="p-6 bg-slate-50 dark:bg-slate-800/20 border border-slate-100 dark:border-slate-800 rounded-2xl">
                                                    <h5 className="font-bold text-base text-slate-900 dark:text-white mb-4">{trade.decision}</h5>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                                                        <div className="p-4 bg-red-500/5 border border-red-500/10 rounded-xl relative overflow-hidden group">
                                                            <span className="text-[10px] font-bold text-red-500 uppercase tracking-widest block mb-2 font-mono">Option A (Rejected)</span>
                                                            <h6 className="font-bold text-sm text-slate-800 dark:text-slate-200 mb-1">{trade.optionA}</h6>
                                                            <p className="text-xs text-muted-foreground">Not chosen due to scale limits or implementation complexity.</p>
                                                        </div>
                                                        <div className="p-4 bg-emerald-500/5 border border-emerald-500/10 rounded-xl relative overflow-hidden group">
                                                            <span className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest block mb-2 font-mono">Option B (Selected)</span>
                                                            <h6 className="font-bold text-sm text-slate-800 dark:text-slate-200 mb-1">{trade.optionB}</h6>
                                                            <p className="text-xs text-muted-foreground">Chosen for reliability, latency requirements, and developer speed.</p>
                                                        </div>
                                                    </div>
                                                    <div className="p-4 bg-white dark:bg-slate-900/50 rounded-xl border border-slate-100 dark:border-slate-800 mt-2">
                                                        <p className="text-xs text-slate-700 dark:text-slate-350 leading-relaxed">
                                                            <span className="font-bold text-primary mr-1.5 font-mono">RATIONALE:</span>
                                                            {trade.reason}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="mt-8 border-b border-slate-100 dark:border-slate-800" />
                                    </section>
                                )}

                                {/* Lessons Learned (Quote Box) */}
                                {project.lessonsLearned && (
                                    <section>
                                        <div className="flex items-center gap-2 mb-4">
                                            <Lightbulb className="h-5 w-5 text-primary" />
                                            <h4 className="font-bold text-lg text-slate-900 dark:text-white">Lessons Learned</h4>
                                        </div>
                                        <div className="bg-primary/5 border border-primary/10 p-6 rounded-2xl">
                                            <p className="text-sm text-slate-700 dark:text-slate-350 leading-relaxed italic">
                                                "{project.lessonsLearned}"
                                            </p>
                                        </div>
                                        <div className="mt-8 border-b border-slate-100 dark:border-slate-800" />
                                    </section>
                                )}

                                {/* Highlights */}
                                {project.highlights && (
                                    <section>
                                        <h4 className="font-bold mb-6 flex items-center gap-2 text-slate-900 dark:text-white text-xl">
                                            <Terminal className="h-5 w-5 text-primary" />
                                            Engineering Highlights
                                        </h4>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            {project.highlights.map((highlight, idx) => (
                                                <div key={idx} className="flex items-start gap-3 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-700 group hover:border-primary/30 transition-colors">
                                                    <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-1 shrink-0" />
                                                    <span className="text-sm text-slate-600 dark:text-slate-400 font-medium leading-relaxed">{highlight}</span>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="mt-10 border-b border-slate-100 dark:border-slate-800" />
                                    </section>
                                )}

                                {/* Screenshots */}
                                {(project.screenshots?.length ?? 0) > 0 && (
                                    <section>
                                        <h4 className="font-bold mb-6 text-xl text-slate-900 dark:text-white">Project Visuals</h4>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                            {project.screenshots?.map((screenshot, idx) => (
                                                <div
                                                    key={idx}
                                                    className="group relative aspect-video rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-2xl transition-all duration-500 cursor-zoom-in"
                                                    onClick={() => setPreviewImage(screenshot)}
                                                >
                                                    <img
                                                        src={screenshot}
                                                        alt={`Screenshot ${idx + 1}`}
                                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                                    />
                                                    <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                                                        <div className="bg-white/20 backdrop-blur-md p-2 rounded-full border border-white/30">
                                                            <Maximize2 className="h-5 w-5 text-white" />
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="mt-10 border-b border-slate-100 dark:border-slate-800" />
                                    </section>
                                )}

                                {/* API Design */}
                                {project.codeSample && (
                                    <section>
                                        <h4 className="font-bold mb-6 text-xl text-slate-900 dark:text-white">Backend Interface</h4>
                                        <div className="rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 shadow-sm bg-slate-900">
                                            <div className="bg-slate-800 dark:bg-slate-950 px-4 py-3 flex items-center justify-between border-b border-slate-700">
                                                <div className="flex items-center gap-2">
                                                    <Code className="h-4 w-4 text-slate-400" />
                                                    <span className="text-xs font-mono font-bold text-slate-300 uppercase tracking-tight">API Interface Definition</span>
                                                </div>
                                                <Badge variant="outline" className="text-[10px] font-mono h-5 bg-slate-900 text-slate-300 border-slate-700">JSON</Badge>
                                            </div>
                                            <div className="p-6 font-mono text-sm overflow-x-auto">
                                                <div className="mb-4">
                                                    <span className="text-emerald-400 font-bold">{project.codeSample.method}</span>
                                                    <span className="ml-2 text-slate-50">{project.codeSample.endpoint}</span>
                                                </div>
                                                <pre className="text-slate-300 overflow-x-auto whitespace-pre-wrap break-all text-xs sm:text-sm max-w-full">
                                                    {project.codeSample.body}
                                                </pre>
                                            </div>
                                        </div>
                                    </section>
                                )}
                            </div>
                        </div>
                    </ScrollArea>

                    {/* Sticky Actions */}
                    <div className="shrink-0 pt-4 pb-6 bg-background/80 backdrop-blur-md border-t border-slate-100 dark:border-slate-800 mt-auto px-6">
                        <div className="flex flex-wrap items-center justify-between gap-4">
                            <div className="flex flex-wrap gap-3 flex-grow sm:flex-grow-0 w-full sm:w-auto">
                                {project.demoUrl ? (
                                    <a href={project.demoUrl} target="_blank" rel="noopener noreferrer" className="flex-1 sm:flex-initial min-w-[120px]">
                                        <Button className="w-full gap-2.5 rounded-xl py-5 font-bold text-sm sm:text-base shadow-lg shadow-primary/20 hover:scale-[1.01] transition-transform">
                                            <ExternalLink className="h-4 w-4" />
                                            Live Demo
                                        </Button>
                                    </a>
                                ) : project.isNDA && (
                                    <div className="flex-1 sm:flex-initial min-w-[180px] bg-slate-100 dark:bg-slate-800/50 rounded-xl px-3 py-2.5 flex items-center justify-center gap-2 border border-slate-200 dark:border-slate-700">
                                        <ShieldAlert className="h-4 w-4 text-slate-400 dark:text-slate-500 shrink-0" />
                                        <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-tight">NDA Protected</span>
                                    </div>
                                )}
                                {project.repoUrl && (
                                    <a href={project.repoUrl} target="_blank" rel="noopener noreferrer" className="flex-1 sm:flex-initial min-w-[120px]">
                                        <Button variant="outline" className="w-full gap-2.5 rounded-xl py-5 font-bold text-sm sm:text-base border-2 hover:bg-slate-50 dark:hover:bg-slate-800 transition-all">
                                            <Github className="h-4 w-4" />
                                            GitHub Repo
                                        </Button>
                                    </a>
                                )}
                            </div>

                            <div className="w-full sm:w-auto">
                                <Button variant="ghost" onClick={onClose} className="w-full sm:w-auto rounded-xl py-5 font-bold text-sm sm:px-6">Close</Button>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Lightbox Preview Dialog */}
            <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
                <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 border-none bg-black/95 overflow-hidden flex items-center justify-center z-[100]">
                    <button
                        onClick={() => setPreviewImage(null)}
                        className="absolute top-4 right-4 z-50 p-2 bg-black/50 text-white rounded-full hover:bg-black/80 transition-colors"
                    >
                        <X className="h-6 w-6" />
                    </button>
                    {previewImage && (
                        <img
                            src={previewImage}
                            alt="Preview"
                            className="max-w-full max-h-full object-contain p-2"
                        />
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}
